import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LayoutGrid, BookOpen, Plus, Trash2, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DARK_INPUT = "rounded-none border-white/20 bg-transparent text-white placeholder:text-white/40";

const ICON_CHOICES = [
  ["syringe", "Syringe"], ["shield", "Shield"], ["heart", "Heart"], ["bandage", "Bandage"],
  ["flask", "Flask"], ["sparkles", "Sparkles"], ["ear", "Ear"], ["leaf", "Leaf"],
  ["activity", "Activity"], ["stethoscope", "Stethoscope"], ["wind", "Wind"], ["droplets", "Droplets"],
];

const KINDS = {
  services: {
    icon: LayoutGrid,
    title: "Services tiles",
    desc: "Edit the tiles in the Pharmacy Services grid — rename, rewrite, add or remove. Each tile links to the booking form.",
    iconField: true,
    urlField: false,
  },
  learn: {
    icon: BookOpen,
    title: "Learn module cards",
    desc: "Edit the Learn with DermaGen cards — rename, rewrite, add new modules or change where each one links.",
    iconField: false,
    urlField: true,
  },
};

export default function ContentManager({ pin, kind }) {
  const cfg = KINDS[kind];
  const [items, setItems] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const res = await axios.get(`${API}/content/${kind}`);
    setItems(res.data.items);
    setDirty(false);
  };

  useEffect(() => {
    load().catch(() => toast.error("Could not load content"));
  }, [kind]);

  const update = (id, field, value) => {
    setItems((list) => list.map((i) => (i.id === id ? { ...i, [field]: value } : i)));
    setDirty(true);
  };

  const add = () => {
    const blank = { id: crypto.randomUUID().slice(0, 8), title: "", desc: "" };
    if (cfg.iconField) blank.icon = "sparkles";
    if (cfg.urlField) blank.url = "";
    setItems((list) => [...list, blank]);
    setDirty(true);
  };

  const remove = (id) => {
    setItems((list) => list.filter((i) => i.id !== id));
    setDirty(true);
  };

  const save = async () => {
    if (items.some((i) => !i.title.trim())) {
      toast.error("Every tile needs a title");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/content/${kind}`, { pin, items });
      toast.success("Saved — live on the site now");
      setDirty(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save — please try again");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-5 border border-white/15 bg-white/5 p-8" data-testid={`content-manager-${kind}`}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-5">
          <div className="border border-white/20 p-3 text-brand-green">
            <cfg.icon className="h-6 w-6" />
          </div>
          <div>
            <h3 className="font-heading text-lg font-bold">{cfg.title}</h3>
            <p className="mt-1 max-w-xl text-sm leading-relaxed text-white/60">{cfg.desc}</p>
          </div>
        </div>
        <div className="flex gap-3">
          <Button onClick={add} variant="outline" data-testid={`content-add-${kind}`} className="rounded-none border-white/25 bg-transparent font-heading font-semibold text-white hover:bg-white/10">
            <Plus className="mr-2 h-4 w-4" /> Add tile
          </Button>
          <Button onClick={save} disabled={saving || !dirty} data-testid={`content-save-${kind}`} className="rounded-none bg-brand-green px-6 font-heading font-semibold text-white hover:bg-brand-greendark disabled:opacity-50">
            <Save className="mr-2 h-4 w-4" /> {saving ? "Saving…" : dirty ? "Save changes" : "Saved"}
          </Button>
        </div>
      </div>

      <div className="mt-6 space-y-3" data-testid={`content-items-${kind}`}>
        {items.map((item) => (
          <div key={item.id} className="border border-white/10 bg-white/5 p-4" data-testid={`content-item-${kind}-${item.id}`}>
            <div className="flex flex-wrap items-center gap-3">
              <Input
                value={item.title}
                onChange={(e) => update(item.id, "title", e.target.value)}
                placeholder="Tile title"
                data-testid={`content-title-${kind}-${item.id}`}
                className={`${DARK_INPUT} max-w-xs font-semibold`}
              />
              {cfg.iconField && (
                <select
                  value={item.icon || "sparkles"}
                  onChange={(e) => update(item.id, "icon", e.target.value)}
                  data-testid={`content-icon-${kind}-${item.id}`}
                  className="h-10 rounded-none border border-white/20 bg-transparent px-3 text-sm text-white"
                >
                  {ICON_CHOICES.map(([v, l]) => <option key={v} value={v} className="text-black">{l}</option>)}
                </select>
              )}
              {cfg.urlField && (
                <Input
                  value={item.url || ""}
                  onChange={(e) => update(item.id, "url", e.target.value)}
                  placeholder="Link URL (https://…)"
                  data-testid={`content-url-${kind}-${item.id}`}
                  className={`${DARK_INPUT} min-w-64 flex-1`}
                />
              )}
              <button
                onClick={() => remove(item.id)}
                data-testid={`content-delete-${kind}-${item.id}`}
                aria-label={`Delete ${item.title || "tile"}`}
                className="ml-auto shrink-0 p-2 text-white/40 transition-colors duration-200 hover:text-red-400"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
            <Textarea
              value={item.desc}
              onChange={(e) => update(item.id, "desc", e.target.value)}
              placeholder="Short description shown on the tile…"
              rows={2}
              data-testid={`content-desc-${kind}-${item.id}`}
              className={`${DARK_INPUT} mt-3`}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
